<template>
  <section class="account-settings-container">
    <Spinner v-if="loading" />
    <div v-else>
      <!-- Page Header -->
      <div class="page-header">
        <div class="header-content">
          <font-awesome-icon :icon="faUserGear" class="header-icon" />
          <div>
            <h1 class="page-title">Hello, {{ userStore.user.username }}</h1>
            <p class="page-subtitle">
              Manage your preferences and security settings
            </p>
          </div>
        </div>
      </div>

      <!-- Portfolio Section -->
      <div class="portfolio-section">
        <div class="portfolio-card">
          <div class="portfolio-info">
            <span class="portfolio-label">Balance</span>
            <div class="portfolio-balance">
              ${{ formatBalance(userStore.user.displayBalance) }}
            </div>
          </div>
          <div class="portfolio-actions">
            <button
              v-if="canUseCashier"
              class="portfolio-btn btn-deposit"
              @click="openDeposit"
            >
              <font-awesome-icon :icon="faArrowDown" class="btn-icon" />
              Deposit
            </button>
            <button
              v-if="canUseCashier"
              class="portfolio-btn btn-withdraw"
              @click="goToWithdraw"
            >
              <font-awesome-icon :icon="faArrowUp" class="btn-icon" />
              Withdraw
            </button>
            <button
              class="portfolio-btn btn-history"
              @click="showTransactionHistory = true"
            >
              <font-awesome-icon :icon="faClockRotateLeft" class="btn-icon" />
              Transaction History
            </button>
            <button
              class="portfolio-btn btn-export"
              title="Export bets to CSV"
              @click="exportModalShown = true"
            >
              <font-awesome-icon :icon="faFileExport" class="btn-icon" />
              Export CSV
            </button>
          </div>
        </div>

      </div>

      <!-- Performance (P&L) mini chart -->
      <div class="portfolio-section">
        <pnl-mini-chart />
      </div>

      <!-- Settings Grid -->
      <div class="settings-grid">
        <!-- Display Preferences Section -->
        <div class="settings-section">
          <div class="section-header">
            <font-awesome-icon :icon="faDesktop" class="section-icon" />
            <h2 class="section-title">Display Preferences</h2>
          </div>
          <div class="settings-card">
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">Display Odds</span>
              </div>
              <div class="setting-control">
                <selector
                  :options="oddsTypes"
                  :selected="oddsType"
                  @update:selected="updateOddsType"
                />
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">Active Theme</span>
                <span class="label-subtext"
                  >Choose your preferred color scheme</span
                >
              </div>
              <div class="setting-control">
                <selector
                  :options="themes"
                  :selected="selectedTheme"
                  @update:selected="updateActiveTheme"
                />
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">Default Odds View</span>
              </div>
              <div class="setting-control">
                <selector
                  :options="oddsViews"
                  :selected="oddsView"
                  @update:selected="updateOddsView"
                />
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  View Odds With Commission?
                  <informational-tooltip
                    text-content="A 1% fee (less of risk/win on losses or win amount on wins) is charged on each taken bet. With this true, odds you see on the exchange will be the price you receive after commission."
                  />
                </span>
              </div>
              <div class="setting-control">
                <selector
                  :options="viewCommissionOptions"
                  :selected="viewOddsWithCommission"
                  @update:selected="handleViewOddsWithCommission"
                />
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  Display Rotation Numbers?
                  <informational-tooltip
                    text-content="Numbers next to team names to quickly identify specific teams or games."
                  />
                </span>
              </div>
              <div class="setting-control">
                <selector
                  :options="displayRotationOptions"
                  :selected="viewDisplayRotation"
                  @update:selected="updateDisplayRotationNumbers"
                />
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  Pro Mode
                  <informational-tooltip
                    text-content="This will instantly display the offer mode in take bet slip. Designed for traders who want to create offers, rather than take offers"
                  />
                </span>
              </div>
              <div class="setting-control">
                <selector
                  :options="proUserOptions"
                  :selected="isProUser"
                  @update:selected="updateIsPro"
                />
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  Default Send Order To Chat
                  <informational-tooltip
                    text-content="This will notify other users about you order"
                  />
                </span>
              </div>
              <div class="setting-control">
                <selector
                  :options="sendOrderToChatOptions"
                  :selected="sendOrderToChat"
                  @update:selected="updateSendOrderToChat"
                />
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  Display Confirmation Ticket
                  <informational-tooltip
                    text-content="Show a detailed bet confirmation popup instead of a simple alert when taking bets"
                  />
                </span>
              </div>
              <div class="setting-control">
                <selector
                  :options="confirmationTicketOptions"
                  :selected="displayConfirmationTicket"
                  @update:selected="updateDisplayConfirmationTicket"
                />
              </div>
            </div>
            <div v-if="ARB_OFFER_ENABLED" class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  Default Arb Offer
                  <informational-tooltip
                    text-content="When on, new offers default to Arb Offer — your order is allowed to rest on the book even when it crosses the opposing side, instead of matching existing liquidity. You can still flip it off per order."
                  />
                </span>
              </div>
              <div class="setting-control">
                <selector
                  :options="arbOfferOptions"
                  :selected="defaultArbOfferSetting"
                  @update:selected="updateDefaultArbOffer"
                />
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  Default Quick-Bet Amount
                  <informational-tooltip
                    text-content="The amount the quick (+) button adds to your Risk or To-Win each tap in the bet slip. Set to Max to fill your max instead."
                  />
                </span>
              </div>
              <div class="setting-control">
                <selector
                  :options="quickBetOptions"
                  :selected="quickBetAmountSetting"
                  @update:selected="updateQuickBetAmount"
                />
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  Custom Quick-Bet Amount
                  <informational-tooltip
                    text-content="Set your own quick-add amount instead of a preset. Saving a custom value overrides the preset selection above."
                  />
                </span>
              </div>
              <div class="setting-control input-group">
                <input
                  v-model="customQuickBet"
                  class="modern-input"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="e.g. 350"
                  @keyup.enter="saveCustomQuickBet"
                />
                <button
                  class="modern-btn btn-primary"
                  @click="saveCustomQuickBet"
                >
                  <font-awesome-icon :icon="faSave" class="btn-icon" />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Security Section -->
        <div class="settings-section security-section">
          <div class="section-header">
            <font-awesome-icon :icon="faShield" class="section-icon" />
            <h2 class="section-title">Security</h2>
          </div>
          <div class="settings-card">
            <div v-if="!userStore.user.userEmail" class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  Recovery Email
                  <informational-tooltip
                    text-content="This is the email that will be used to reset your password."
                  />
                </span>
              </div>
              <div class="setting-control input-group">
                <input
                  id="email"
                  v-model="email"
                  type="email"
                  class="modern-input"
                  name="email"
                  placeholder="your@email.com"
                />
                <input
                  id="email-password"
                  v-model="emailPassword"
                  type="password"
                  class="modern-input"
                  name="current-password"
                  autocomplete="current-password"
                  placeholder="Account password"
                />
                <button
                  class="modern-btn btn-primary"
                  :disabled="!email || !emailPassword"
                  @click="saveEmail"
                >
                  <font-awesome-icon :icon="faSave" class="btn-icon" />
                  Save
                </button>
              </div>
            </div>
            <!-- Always available — the enroll popup handles users with no email
                 (it collects one as a first step), so this must never be hidden
                 behind having an email on file. -->
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  Two Factor Authentication
                  <informational-tooltip
                    text-content="2FA is required to withdraw to a new crypto address. Choose an authenticator app or email codes."
                  />
                </span>
                <span class="label-subtext">{{ twoFactorStatusLabel }}</span>
              </div>
              <div class="setting-control">
                <button class="modern-btn btn-primary" @click="openTwoFactorEnroll">
                  <font-awesome-icon :icon="faShieldCheck" class="btn-icon" />
                  {{ currentTwoFactorMethod ? 'Change 2FA Method' : 'Set Up 2FA' }}
                </button>
              </div>
            </div>
            <div
              v-if="userStore.user.userEmail"
              class="setting-item setting-row"
            >
              <div class="setting-label flex-shrink-0 justify-content-center">
                <span class="label-text">Email</span>
                <span
                  class="label-subtext font-weight-bold"
                  :class="{
                    'success-text': userStore.user.emailConfirmation,
                    'warning-text': !userStore.user.emailConfirmation,
                  }"
                >
                  {{
                    userStore.user.emailConfirmation
                      ? 'Verified'
                      : 'Not verified'
                  }}
                </span>
              </div>
              <div class="setting-control">
                <div
                  class="modern-input d-flex align-items-center justify-content-between"
                  :class="{
                    'border-warning': !userStore.user.emailConfirmation,
                    'border-success': userStore.user.emailConfirmation,
                  }"
                >
                  <input
                    id="user-verification-email"
                    v-model="email"
                    class="p-0"
                    readonly
                    placeholder="0"
                  />
                  <font-awesome-icon
                    :icon="
                      userStore.user.emailConfirmation
                        ? faCheck
                        : faExclamationTriangle
                    "
                    :class="{
                      'text-warning': !userStore.user.emailConfirmation,
                      'text-success': userStore.user.emailConfirmation,
                    }"
                  />
                </div>
                <button
                  v-if="!userStore.user.emailConfirmation && resendCooldown <= 0"
                  class="modern-btn btn-primary"
                  :disabled="disableResendConfirmationEmail"
                  @click="resendConfirmationEmail"
                >
                  Resend Confirmation
                </button>
                <span
                  v-if="resendCooldown > 0"
                  class="resend-confirmation-text"
                >
                  Confirmation email sent!
                </span>
              </div>
            </div>
            <div
              id="password-section"
              class="setting-item password-section"
              :class="{ 'needs-attention': passwordNeedsAttention }"
            >
              <div class="setting-label">
                <span class="label-text">
                  Password Management
                  <span v-if="passwordNeedsAttention" class="attention-pill">
                    <font-awesome-icon :icon="faExclamationTriangle" />
                    Recommended
                  </span>
                </span>
                <span class="label-subtext">
                  {{
                    passwordNeedsAttention
                      ? 'We recommend changing your password.'
                      : 'Update your account password'
                  }}
                </span>
              </div>
              <div class="password-actions">
                <button
                  class="modern-btn"
                  :class="passwordNeedsAttention ? 'btn-attention' : 'btn-primary'"
                  @click="showChangePasswordModal = true"
                >
                  <font-awesome-icon :icon="faKey" class="btn-icon" />
                  Change Password
                </button>
                <button
                  class="modern-btn btn-outline"
                  @click="initiatePasswordResetModalShown = true"
                >
                  <font-awesome-icon :icon="faRotateLeft" class="btn-icon" />
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Betting Preferences Section -->
        <div class="settings-section">
          <div class="section-header">
            <font-awesome-icon :icon="faDiceD6" class="section-icon" />
            <h2 class="section-title">Betting Preferences</h2>
          </div>
          <div class="settings-card">
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  Max Bet ($)
                  <informational-tooltip
                    text-content="This is to prevent you from entering a bet that is too large. The maximum allowed is $100,000"
                  />
                </span>
              </div>
              <div class="setting-control input-group">
                <input
                  id="maxLiability"
                  v-model="maxLiability"
                  class="modern-input"
                  type="number"
                  max="100000"
                  min="0"
                  name="maxLiability"
                  placeholder="0"
                />
                <button
                  class="modern-btn btn-primary"
                  @click="saveMaxLiability"
                >
                  <font-awesome-icon :icon="faSave" class="btn-icon" />
                  Save
                </button>
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  Live Bet Odds Slippage
                  <informational-tooltip
                    text-content="Live odds move fast. Never miss a live bet to a tiny price move."
                  />
                </span>
              </div>
              <div class="setting-control">
                <selector
                  :options="liveSlippageOptions"
                  :selected="liveSlippageMode"
                  @update:selected="handleLiveSlippageMode"
                />
              </div>
            </div>
            <!-- Slider is the control: releasing it saves. Dragging to 0 flips
                 the toggle above to "No Slippage"; any value > 0 flips it to
                 "Accept Slippage". No separate Save button. -->
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  Acceptable slippage
                  <informational-tooltip
                    text-content="How far a live bet may fill past your price before it cancels. The table shows the worst odds each line would book at. Slide to 0 to turn slippage off."
                  />
                </span>
              </div>
              <div class="setting-control">
                <div style="display: flex; align-items: center; gap: 0.75rem">
                  <input
                    id="liveSlippagePct"
                    type="range"
                    class="slippage-slider"
                    min="0"
                    :max="liveSlippageMaxPct"
                    step="0.5"
                    :value="liveSlippagePct"
                    :style="{ '--fill': sliderFillStop }"
                    @input="liveSlippagePct = Number($event.target.value)"
                    @change="commitLiveSlippage"
                  />
                  <span
                    style="
                      min-width: 3rem;
                      text-align: right;
                      font-weight: 700;
                      color: var(--color-primary_01);
                    "
                    >{{ liveSlippagePct }}%</span
                  >
                </div>
                <div v-if="slippageActive" class="slippage-examples">
                  <div
                    v-for="row in liveSlippagePreview"
                    :key="row.requested"
                    class="slippage-example-cell"
                  >
                    <span class="req">{{ row.requestedDisplay }}</span>
                    <span class="arrow">→</span>
                    <span class="max">{{ row.newMaxDisplay }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">Default Create Offer Risk</span>
              </div>
              <div class="setting-control input-group">
                <input
                  id="defaultOffer"
                  v-model="defaultOffer"
                  class="modern-input"
                  type="text"
                  name="defaultOffer"
                  placeholder="0"
                />
                <button
                  class="modern-btn btn-primary"
                  @click="saveDefaultOffer"
                >
                  <font-awesome-icon :icon="faSave" class="btn-icon" />
                  Save
                </button>
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">
                  Create Offer Unit Mode
                  <informational-tooltip wide>
                    <div class="unit-mode-tip">
                      <div class="unit-mode-tip__lead">
                        At a <strong>$100</strong> unit (risk / win):
                      </div>
                      <table class="unit-mode-tip__table">
                        <thead>
                          <tr>
                            <th></th>
                            <th>−110</th>
                            <th>+110</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <th>Risk a Unit</th>
                            <td>$100 / $90.91</td>
                            <td>$100 / $110</td>
                          </tr>
                          <tr>
                            <th>Win a Unit</th>
                            <td>$110 / $100</td>
                            <td>$90.91 / $100</td>
                          </tr>
                          <tr>
                            <th>The Cardano</th>
                            <td>$110 / $100</td>
                            <td>$100 / $110</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </informational-tooltip>
                </span>
              </div>
              <div class="setting-control">
                <selector
                  :options="createOfferUnitModeOptions"
                  :selected="viewCreateOfferUnitMode"
                  @update:selected="updateCreateOfferUnitMode"
                />
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">Default Order Expiry (minutes)</span>
              </div>
              <div class="setting-control input-group">
                <input
                  id="defaultExpiry"
                  v-model="defaultExpiry"
                  class="modern-input"
                  type="text"
                  name="defaultExpiry"
                  placeholder="0"
                />
                <button
                  class="modern-btn btn-primary"
                  @click="saveDefaultExpiry"
                >
                  <font-awesome-icon :icon="faSave" class="btn-icon" />
                  Save
                </button>
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">Sort Games By</span>
              </div>
              <div class="setting-control">
                <select
                  id="rotationN"
                  v-model="sortByValue"
                  name="sortBy"
                  class="modern-select"
                  @change="updateSortBy()"
                >
                  <option v-for="value in sortBy" :key="value" :value="value">
                    {{ value.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">Matched Order Notifications</span>
                <span class="label-subtext">Get notified via Telegram</span>
              </div>
              <div class="setting-control input-group">
                <input
                  v-model="telegramUsername"
                  type="text"
                  placeholder="Enter Telegram Username"
                  class="modern-input"
                />
                <button
                  class="modern-btn btn-primary"
                  @click="subscribeTelegram"
                >
                  <font-awesome-icon :icon="faSave" class="btn-icon" />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Bet From Telegram Section -->
        <div class="settings-section">
          <div class="section-header">
            <font-awesome-icon :icon="faPaperPlane" class="section-icon" />
            <h2 class="section-title">Bet From Telegram</h2>
          </div>
          <div class="settings-card">
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">Telegram access</span>
                <span class="label-subtext">
                  Trade the exchange from the Telegram app.
                  <a
                    href="/blog/telegram-betting-is-live"
                    class="telegram-setting__inline-link"
                    >Read more</a
                  >
                </span>
              </div>
              <div class="setting-control">
                <span v-if="telegramAccess.loading" class="telegram-setting__status">
                  Checking…
                </span>
                <span
                  v-else-if="telegramAccess.requested"
                  class="telegram-setting__status telegram-setting__status--ok"
                >
                  Requested · ID {{ telegramAccess.telegramId }}
                </span>
              </div>
            </div>

            <div v-if="!telegramAccess.loading && !telegramAccess.requested" class="setting-item">
              <div class="setting-label">
                <span class="label-text">How to get access</span>
                <ol class="telegram-setting__steps">
                  <li>
                    Open
                    <a
                      href="https://t.me/castersTrade_bot"
                      target="_blank"
                      rel="noopener"
                      class="telegram-setting__inline-link"
                      >@castersTrade_bot</a
                    >
                    in Telegram.
                  </li>
                  <li>Send <code>/start</code>. It replies with your ID number.</li>
                  <li>Paste that ID here and request access.</li>
                </ol>
              </div>
              <div class="setting-control">
                <form class="telegram-setting__form" @submit.prevent="submitTelegramId">
                  <input
                    v-model="telegramAccess.id"
                    class="telegram-setting__input"
                    type="text"
                    inputmode="numeric"
                    placeholder="e.g. 690013402"
                    aria-label="Your Telegram ID"
                    :disabled="telegramAccess.sending"
                  />
                  <button
                    class="telegram-setting__toggle"
                    type="submit"
                    :disabled="telegramAccess.sending || !telegramAccess.id"
                  >
                    {{ telegramAccess.sending ? 'Sending…' : 'Request access' }}
                  </button>
                </form>
                <span v-if="telegramAccess.error" class="telegram-setting__error">
                  {{ telegramAccess.error }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Advanced Settings -->
        <div
          v-if="
            userStore.user.username === 'poly' ||
            userStore.user.username === 'betwar' ||
            userStore.user.dgsSeeder
          "
          class="settings-section"
        >
          <div class="section-header">
            <font-awesome-icon :icon="faCog" class="section-icon" />
            <h2 class="section-title">Advanced Settings</h2>
          </div>
          <div class="settings-card">
            <div
              v-if="
                userStore.user.username === 'poly' ||
                userStore.user.username === 'betwar'
              "
              class="setting-item"
            >
              <div class="setting-label">
                <span class="label-text">Account Status</span>
                <span class="label-subtext">Lock or unlock your account</span>
              </div>
              <div class="setting-control">
                <span
                  class="status-badge"
                  :class="{
                    'badge-enabled': selectedLockStatus?.key === 'unlocked',
                    'badge-disabled badge-warning':
                      selectedLockStatus?.key === 'locked',
                  }"
                >
                  {{
                    selectedLockStatus?.key === 'unlocked'
                      ? 'Unlocked'
                      : 'Locked'
                  }}
                </span>
                <selector
                  :options="lockStatusOptions"
                  :selected="selectedLockStatus"
                  @update:selected="updateLockStatus"
                />
              </div>
            </div>
            <div v-if="userStore.user.dgsSeeder" class="setting-item">
              <div class="setting-label">
                <span class="label-text">3rd Party Seeder</span>
              </div>
              <div class="setting-control">
                <selector
                  :options="dgsTypes"
                  :selected="dgsType"
                  @update:selected="updateDgsType"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Close Account Section -->
        <div class="settings-section">
          <div class="settings-card">
            <div class="setting-item">
              <div class="setting-label">
                <span class="label-text">Close Account</span>
                <span class="label-subtext danger-text"
                  >You will need to message an admin to re open</span
                >
              </div>
              <div class="setting-control">
                <button class="modern-btn btn-danger" @click="closeAccount">
                  <font-awesome-icon :icon="faTrashCan" class="btn-icon" />
                  Close Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- <div
      class="my-3"
      style="display: flex; justify-content: center;"
    >
      <button
        style="border-radius: 5px !important;"
        @click="initiatePasswordResetModalShown = true"
      >
        Reset password
      </button>
    </div> -->
    <change-password-modal
      v-if="showChangePasswordModal"
      @close-modal="showChangePasswordModal = false"
    />
    <initiate-reset-password-modal
      v-if="initiatePasswordResetModalShown"
      @close-modal="initiatePasswordResetModalShown = false"
      @send-email-fail="sendEmailFail"
      @send-email-success="sendEmailSuccess"
    />
    <export-bets-modal
      v-if="exportModalShown"
      @close-modal="exportModalShown = false"
    />
    <modal
      v-if="showTransactionHistory"
      @close-modal="showTransactionHistory = false"
    >
      <h3 class="transaction-history-title">Transaction History</h3>
      <wallet-transactions />
    </modal>
  </section>
</template>

<script setup>
// core
import { ref, onBeforeMount, onMounted, onUnmounted, watch, computed, nextTick } from 'vue';
import Swal from 'sweetalert2';
import { useAPI } from '@/composables/useAPI';
import {
  oddsViews,
  oddsTypes,
  twoFATypes,
  sortBy,
  displayRotationOptions,
  viewCommissionOptions,
  liveSlippageOptions,
  liveSlippageExampleOdds,
  liveSlippageMaxPct,
  createOfferUnitModeOptions,
  ARB_OFFER_ENABLED,
} from '../../../consts/constants';
import { useRouter } from 'vue-router';

// components
import Spinner from '@/components/global/Spinner.vue';
import PnlMiniChart from '@/components/userBets/PnlMiniChart.vue';
import Selector from '@/components/global/Selector.vue';
import InformationalTooltip from '@/components/global/tooltips/InformationalTooltip.vue';
import { emitter } from '@/core/emitter';
import { useUserStore } from '@/stores/user';
import { useTwoFactor } from '@/composables/useTwoFactor';
import { PostHogUtils } from '@/core/PostHogUtils';
import {
  canonicalSortFromUser,
  slippageFloorOdds,
  convertAmericanToPercent,
  convertPercentToAmerican,
} from '@/core/utils';
import {
  oddsWithCommission,
  impliedProbWithoutCommission,
  rawSlippageForDisplayedTolerance,
} from '@/core/commissionUtils';
import { userCanUseCashier } from '@/consts/constants';
import changePasswordModal from '@/components/account/changePasswordModal.vue';
import InitiateResetPasswordModal from '@/components/account/InitiatePasswordResetModal.vue';
import ExportBetsModal from '@/components/userBets/modals/ExportBetsModal.vue';
import WalletTransactions from '@/components/userBets/WalletTransactions.vue';
import Modal from '@/components/global/Modal.vue';

// icons
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faUserGear } from '@fortawesome/pro-light-svg-icons/faUserGear';
import { faDesktop } from '@fortawesome/pro-light-svg-icons/faDesktop';
import {
  faShield,
  faShieldCheck,
} from '@fortawesome/pro-light-svg-icons/faShield';
import { faDiceD6 } from '@fortawesome/pro-light-svg-icons/faDiceD6';
import { faCog } from '@fortawesome/pro-light-svg-icons/faCog';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { faSave } from '@fortawesome/pro-light-svg-icons/faSave';
import { faKey } from '@fortawesome/pro-light-svg-icons/faKey';
import { faRotateLeft } from '@fortawesome/pro-light-svg-icons/faRotateLeft';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons/faExclamationTriangle';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons/faArrowDown';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons/faArrowUp';
import { faFileExport } from '@fortawesome/free-solid-svg-icons/faFileExport';
import { faClockRotateLeft } from '@fortawesome/free-solid-svg-icons/faClockRotateLeft';

const userStore = useUserStore();
const { getActiveMethod } = useTwoFactor();

// True when login flagged the password as unsafe. Any one of the server's
// passwordSecurityChecks can trip this (breached, too short, contains the
// username), so the copy it drives must stay generic about the reason.
// Drives the amber "needs attention" treatment on the password block.
const passwordNeedsAttention = computed(() => userStore.passwordHighlighted);

// Withdraw is hidden for users blocked from the cashier (blocked affiliate
// codes / usernames) — same rule as the cashier nav link and route guard.
const canUseCashier = computed(() => userCanUseCashier(userStore.user));

// Map raw setting values (often booleans) to human-readable labels so the
// setting_changed events read unambiguously in the analytics dashboards
// (e.g. "Pro"/"Standard" instead of true/false). Labels are event-only:
// person properties always carry the raw values so they can never diverge
// from the login/getMe snapshot (buildSettingsSnapshot in stores/user.js).
// Per-setting label maps; settings not listed here pass through unchanged.
const SETTING_VALUE_LABELS = {
  // Pro Mode is three-way; the selected option name is reported directly
  // ('Yes' / 'No' / 'Pregame Only'), so no boolean mapping applies here.
  view_odds_with_commission: { true: 'With commission', false: 'Without commission' },
  display_rotation_numbers: { true: 'Rotation #s shown', false: 'Rotation #s hidden' },
  default_arb_offer: { true: 'Arb offer on', false: 'Arb offer off' },
  display_confirmation_ticket: { true: 'Confirmation shown', false: 'Confirmation hidden' },
  default_send_order_to_chat: { true: 'Sends to chat', false: 'No chat' },
  default_odds_view: { sportsbook: 'Sportsbook', exchange: 'Exchange' },
  active_theme: { day: 'Day', night: 'Night' },
};

function labelSettingValue(name, value) {
  const map = SETTING_VALUE_LABELS[name];
  if (!map) return value;
  return map[String(value)] ?? value;
}

// Coerce an expiry value to a finite number of minutes, keeping "never set"
// as null. Number(null) and Number('') are 0, so a plain Number() cast would
// report an unset expiry as "expires in 0 minutes" on the analytics profile.
function normalizeExpiryMinutes(value) {
  if (value === null || value === undefined || value === '') return null;
  const minutes = Number(value);
  return Number.isFinite(minutes) ? minutes : null;
}

// Fire a setting_changed event AND mirror the new value onto the person
// profile so we get both "how often switched" and "% of users using X".
// The event gets the human-readable label; the person property gets the RAW
// value — buildSettingsSnapshot (stores/user.js) rewrites the same keys with
// raw values on every login/getMe identify, so labeled values here would
// fragment the distribution charts across two formats.
function trackSetting(name, fromValue, toValue) {
  const from = labelSettingValue(name, fromValue);
  const to = labelSettingValue(name, toValue);
  PostHogUtils.captureSettingChange(name, from, to);
  PostHogUtils.syncSettings({ [name]: toValue });
}

const loading = ref(false);
const sportsbookMinimumDisplay = ref(userStore.user.sportsbookMinimumDisplay); // user.sportsbookMinimumDisplay
const emit = defineEmits(['closePopUp']);
const router = useRouter();

// Portfolio functions
function formatBalance(balance) {
  const num = parseFloat(balance);
  if (isNaN(num)) return '0.00';
  // A balance that rounds away to zero (e.g. -0.000321) has no sign left to
  // show — toLocaleString would otherwise print it as "-0.00".
  const forDisplay = Number(num.toFixed(2)) === 0 ? 0 : num;
  return forDisplay.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function openDeposit() {
  emitter.emit('showDepositModal');
}

function goToWithdraw() {
  emitter.emit('showWithdrawModal');
}

// User lock status for poly user
const isUserLocked = ref(false);

const exportModalShown = ref(false);
const showTransactionHistory = ref(false);

const disableResendConfirmationEmail = ref(false);
const resendCooldown = ref(0);
let resendCooldownInterval = null;
// get init settings
onBeforeMount(() => {
  initSettings();
  getSettings();
  restoreResendCooldown();
  if (
    userStore.user.username === 'poly' ||
    userStore.user.username === 'betwar'
  ) {
    getLockStatus();
  }
});
// Scroll to password section if redirected from unsafe password notification

// ---- Telegram bot access -------------------------------------------------
// Same request the home-page banner makes, kept here so it stays reachable
// once that banner has been dismissed.
const telegramAccess = ref({
  loading: true,
  requested: false,
  telegramId: null,
  id: '',
  sending: false,
  error: '',
});

async function loadTelegramAccess() {
  try {
    const { get } = useAPI('/user/telegramAccessStatus');
    const data = await get();
    telegramAccess.value.requested = Boolean(data?.requested);
    telegramAccess.value.telegramId = data?.telegramId ?? null;
  } catch {
    // Status is a nicety: on failure just offer the form rather than an error.
  } finally {
    telegramAccess.value.loading = false;
  }
}

async function submitTelegramId() {
  const state = telegramAccess.value;
  if (state.sending) return;
  state.error = '';

  const id = String(state.id || '').trim();
  // Mirrors the API's own check so a typo doesn't cost a round trip.
  if (!/^\d{5,20}$/.test(id)) {
    state.error = 'That should be the number the bot sends you, digits only.';
    return;
  }

  state.sending = true;
  try {
    const { request } = useAPI('/user/requestTelegramAccess');
    const response = await request({ telegramId: id });
    // Matches the banner's guard. The endpoint only ever returns received:true
    // today, but treating any non-throwing response as success means a soft
    // failure would flip the UI to "Requested" and hide the form — telling the
    // user they are on the list when they are not.
    if (response?.received === false) {
      throw new Error("That didn't go through. Try again in a moment.");
    }
    state.requested = true;
    state.telegramId = id;
    PostHogUtils.captureBannerClicked(
      'telegram_access',
      'access_requested_settings',
    );
  } catch (err) {
    // useAPI unwraps failures onto err.message; err.response is always undefined.
    state.error = err?.message || "Couldn't send that. Try again in a moment.";
  } finally {
    state.sending = false;
  }
}

onMounted(loadTelegramAccess);

onMounted(() => {
  const shouldScroll = sessionStorage.getItem('scrollToPasswordSection');
  if (shouldScroll) {
    sessionStorage.removeItem('scrollToPasswordSection');
    setTimeout(() => {
      const element = document.getElementById('password-section');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 500);
  }
  refreshTwoFactorMethod();
  // The enroll modal is mounted globally; refresh our status when it reports a
  // change so the label/button update without a page reload.
  emitter.on('twoFactorMethodChanged', refreshTwoFactorMethod);
});

onUnmounted(() => {
  emitter.off('twoFactorMethodChanged', refreshTwoFactorMethod);
});

// password reset modal
const initiatePasswordResetModalShown = ref(false);
const showChangePasswordModal = ref(false);

async function getSettings() {
  try {
    loading.value = true;
    if (userStore.user.dgsSeeder) {
      await getDgsSeederEnabled();
    }

    const { request } = useAPI('/user/getAdvancedSettings');
    const response = await request();
    oddsView.value = response.sportsbookDefault ? oddsViews[0] : oddsViews[1];
    if (response.oddsFormat === 'american') {
      oddsType.value = oddsTypes[0];
    } else if (response.oddsFormat === 'decimal') {
      oddsType.value = oddsTypes[1];
    } else if (response.oddsFormat === 'impliedProbability') {
      oddsType.value = oddsTypes[2];
    }
    defaultOffer.value = response.defaultOffer;
    defaultExpiry.value = response.defaultExpiry;
    maxLiability.value = response.maxLiability;
    twoFAType.value =
      response.twoFAType === 'telegram' ? twoFATypes[1] : twoFATypes[0];
    email.value = response.email;
    isProUser.value = resolveProUserOption(response.isPro, response.isProLive);
    sendOrderToChat.value = response.defaultSendOrderMessage
      ? sendOrderToChatOptions[0]
      : sendOrderToChatOptions[1];
    viewDisplayRotation.value = userStore.user.displayRotationNumbers
      ? displayRotationOptions[0]
      : displayRotationOptions[1];
    // Resolve the mode once and drive BOTH the selector and the store from it,
    // so they can never disagree: prefer the API value, fall back to whatever
    // getMe already put on the store, then the default. This avoids clobbering
    // the stored mode with an absent field and keeps the Account selector in
    // sync with the mode Create Offer prefill reads.
    const resolvedUnitMode =
      response.createOfferUnitMode ||
      userStore.user.createOfferUnitMode ||
      'riskAUnit';
    viewCreateOfferUnitMode.value =
      createOfferUnitModeOptions.find((o) => o.key === resolvedUnitMode) ||
      createOfferUnitModeOptions[0];
    userStore.user.createOfferUnitMode = resolvedUnitMode;
    viewOddsWithCommission.value = response.viewOddsWithCommission
      ? viewCommissionOptions[0]
      : viewCommissionOptions[1];
    // Live slippage is device-local (100% frontend — no server field): the
    // controls seed from the store, which the localStorage snapshot hydrates.
    liveSlippageMode.value =
      userStore.user.liveSlippageMode === 'slippage'
        ? liveSlippageOptions[1]
        : liveSlippageOptions[0];
    liveSlippagePct.value = clampSlippage(userStore.user.liveSlippagePct);
    // Load confirmation ticket setting (from API or localStorage)
    const savedSetting = localStorage.getItem('displayConfirmationTicket');
    const apiSetting = response.displayConfirmationTicket;
    const finalSetting =
      apiSetting !== undefined ? apiSetting : savedSetting === 'true';

    displayConfirmationTicket.value = finalSetting
      ? confirmationTicketOptions[0]
      : confirmationTicketOptions[1];

    // Update user store with the setting
    userStore.user.displayConfirmationTicket = finalSetting;
    userStore.user.emailConfirmation = response.emailConfirmation;
    if (ARB_OFFER_ENABLED && response.defaultArbOffer !== undefined) {
      userStore.user.defaultArbOffer = !!response.defaultArbOffer;
      defaultArbOfferSetting.value = response.defaultArbOffer
        ? arbOfferOptions[0]
        : arbOfferOptions[1];
      try {
        localStorage.setItem(
          'defaultArbOffer',
          response.defaultArbOffer ? 'true' : 'false',
        );
      } catch (err) {
        // ignore storage errors
      }
    }

    userStore.setUserEmail(response.email);

    // Push a full, accurate settings snapshot onto the person profile whenever
    // the account page loads — powers the "% of users using X" distributions.
    // Values use the same RAW shapes as buildSettingsSnapshot (stores/user.js),
    // which rewrites these keys on every login/getMe identify — labeled values
    // here would fragment the distribution charts across two formats.
    PostHogUtils.syncSettings({
      display_odds: response.oddsFormat,
      default_odds_view: response.sportsbookDefault ? 'sportsbook' : 'exchange',
      view_odds_with_commission: !!response.viewOddsWithCommission,
      display_rotation_numbers: !!userStore.user.displayRotationNumbers,
      pro_mode: resolveProUserOption(response.isPro, response.isProLive).name,
      default_send_order_to_chat: !!response.defaultSendOrderMessage,
      display_confirmation_ticket: !!finalSetting,
      default_arb_offer: !!response.defaultArbOffer,
      active_theme: userStore.activeTheme,
      default_order_expiry_minutes: normalizeExpiryMinutes(response.defaultExpiry),
      live_slippage_mode:
        userStore.user.liveSlippageMode === 'slippage' ? 'slippage' : 'strict',
      live_slippage_pct: Number.isFinite(Number(userStore.user.liveSlippagePct))
        ? Number(userStore.user.liveSlippagePct)
        : null,
    });
  } catch (error) {
    console.error('Error fetching settings ');
  } finally {
    loading.value = false;
  }
}

function sendEmailFail() {
  initiatePasswordResetModalShown.value = false;
}

function sendEmailSuccess() {
  initiatePasswordResetModalShown.value = false;
}

async function getDgsSeederEnabled() {
  try {
    const { request } = useAPI('/user/getDGSStatus');
    const result = await request();
    dgsType.value = result.dgsSeederEnabled ? dgsTypes[0] : dgsTypes[1];
  } catch (error) {
    console.log('error getting dgs seeder enabled ');
  }
}

function initSettings() {
  if (userStore.user.defaultRotationNumbers) {
    sortByValue.value = sortBy[1];
  } else if (userStore.user.matchedVolume) {
    sortByValue.value = sortBy[2];
  } else if (userStore.user.openInterest) {
    sortByValue.value = sortBy[3];
  } else {
    sortByValue.value = sortBy[0];
  }
}

// max liablity
const maxLiability = ref('');
watch(maxLiability, (newVal, oldVal) => {
  if (newVal > 100000) {
    maxLiability.value = 100000;
    // show alert
    emitter.emit('showAlert', {
      type: 'warning',
      shown: true,
      status: 'cancel',
      title: 'Warning',
      detail: 'The maximum allowed is $100,000',
    });
  }
});
function saveMaxLiability() {
  Swal.fire({
    html: '<h4>Do you want to save the changes?</h4>',
    showCancelButton: true,
    confirmButtonText: 'Save',
    confirmButtonColor: '#6f75b1',
  }).then((result) => {
    if (result.isConfirmed) {
      const { request } = useAPI('/user/updateMaxLiability');
      request({ maxLiability: maxLiability.value }).then((response) => {
        if (response.success) {
          userStore.user.maxLiability = maxLiability.value;
        }
        // show alert
        emitter.emit('showAlert', {
          type: 'profile-updated',
          shown: true,
          status: 'ok',
          title: 'Saved configuration',
          detail: '',
        });
      });
    }
  });
}

// Live bet odds slippage. The slider is the single source of truth: 0 = No
// Slippage, > 0 = Accept Slippage at that %. Releasing the slider persists.
const liveSlippageMode = ref();
// Init 0, not '' — an empty string renders the native range at its midpoint
// (5%) before settings load; a save in that state would persist a rendering
// artifact (spec-review minor).
const liveSlippagePct = ref(0);
// Values persisted under the old 10% cap are clamped for display so the
// slider never renders out of range; the server clamps independently at
// booking time (MaxSlippagePoints).
const clampSlippage = (v) =>
  Math.max(0, Math.min(liveSlippageMaxPct, Number(v) || 0));
// "Accept Slippage" active whenever the tolerance is above zero.
const slippageActive = computed(() => Number(liveSlippagePct.value) > 0);
// Cross-tab truth: another tab's save lands in the store via the dedicated
// liveSlippage-key sync; mirror it into this page's local controls so an open
// settings tab can never display — or re-save — a stale tolerance (bugbot).
// IMMEDIATE (spec-review 25 G4): the setting is device-local and must never
// depend on the unrelated getAdvancedSettings call succeeding — a failed
// server fetch left the controls lying at "No Slippage/0%" over a saved 5%,
// and touching the lying toggle then overwrote the real setting.
watch(
  () => [userStore.user.liveSlippageMode, userStore.user.liveSlippagePct],
  ([mode, pct]) => {
    liveSlippagePct.value = clampSlippage(pct);
    liveSlippageMode.value =
      mode === 'slippage' ? liveSlippageOptions[1] : liveSlippageOptions[0];
  },
  { immediate: true },
);
// Where the track fill ends. The thumb (16px) travels the track minus its own
// width, so the fill stop must use the same inset math or it drifts from the
// thumb: center = f·100% − (f − 0.5)·16px. Flush at both ends.
const sliderFillStop = computed(() => {
  const f = Math.max(
    0,
    Math.min(1, (Number(liveSlippagePct.value) || 0) / liveSlippageMaxPct),
  );
  return `calc(${(f * 100).toFixed(2)}% - ${((f - 0.5) * 16).toFixed(1)}px)`;
});
function formatExampleOdds(odds) {
  const rounded = Math.round(odds);
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}
// Example anchors are prices AS THE USER SEES THEM. For commission-view users
// each displayed anchor is mapped to its raw book price (standard commission
// rate — this page has no league context), the raw floor is computed exactly
// as the bet path computes it (rawSlippageForDisplayedTolerance →
// slippageFloorOdds), and the result is shown back on the same adjusted
// scale. Non-commission users: pure raw, unchanged.
const liveSlippagePreview = computed(() => {
  const pct = Number(liveSlippagePct.value) || 0;
  const commissionView = !!userStore.user.viewOddsWithCommission;
  const c = Number(userStore.user.commissionCharged) || 0.01;
  return liveSlippageExampleOdds.map((displayOdds) => {
    const rawAnchor = commissionView
      ? convertPercentToAmerican(
          impliedProbWithoutCommission(
            convertAmericanToPercent(displayOdds),
            c,
          ),
        )
      : displayOdds;
    const rawFloor = slippageFloorOdds(
      rawAnchor,
      rawSlippageForDisplayedTolerance(rawAnchor, pct),
    );
    const newMaxOdds = commissionView
      ? oddsWithCommission(rawFloor, undefined, undefined, 'precise')
      : rawFloor;
    return {
      requested: displayOdds,
      requestedDisplay: formatExampleOdds(displayOdds),
      newMaxDisplay: formatExampleOdds(newMaxOdds),
    };
  });
});
// Device-local save (CTO directive: 100% frontend). The store action writes
// the fields + the user-scoped localStorage snapshot synchronously — a save
// cannot fail and there is no server round-trip, so no optimistic writes,
// rollback anchors, save sequences, or getMe guards are needed.
function persistLiveSlippage(modeKey, pct) {
  const fromMode = userStore.user.liveSlippageMode || 'strict';
  // Analytics: prior tolerance, read before the store write below.
  const fromPct = Number(userStore.user.liveSlippagePct) || 0;
  userStore.saveLiveSlippageSetting(modeKey, pct);
  trackSetting('live_slippage_mode', fromMode, modeKey);
  // Rich change event: pct deltas + surface — the generic event above keys
  // on mode only, so a 2→5% slide (mode unchanged) is invisible to it.
  PostHogUtils.capture('slippage_setting_changed', {
    from_mode: fromMode,
    to_mode: modeKey,
    from_pct: fromPct,
    to_pct: pct,
    source: 'settings',
  });
  // Sync pct too — trackSetting only syncs the mode, so the tolerance
  // person property otherwise refreshes only on a settings-page load.
  PostHogUtils.syncSettings({ live_slippage_pct: pct });
  emitter.emit('showAlert', {
    type: 'profile-updated',
    shown: true,
    status: 'ok',
    title: 'Saved configuration',
    detail: '',
  });
}
// Toggle: "No Slippage" zeroes the slider; "Accept Slippage" seeds a default
// (1%) when there's no prior amount. Persists directly.
function handleLiveSlippageMode(value) {
  let pct;
  if (value.key === 'slippage') {
    pct = Number(liveSlippagePct.value) > 0 ? Number(liveSlippagePct.value) : 1;
  } else {
    pct = 0;
  }
  liveSlippagePct.value = pct;
  liveSlippageMode.value = value;
  persistLiveSlippage(value.key, pct);
}
// Releasing the slider is the save. 0 flips the toggle to No Slippage; any
// value > 0 flips it to Accept Slippage.
function commitLiveSlippage() {
  const pct = Number(liveSlippagePct.value) || 0;
  const modeKey = pct > 0 ? 'slippage' : 'strict';
  liveSlippageMode.value =
    modeKey === 'slippage' ? liveSlippageOptions[1] : liveSlippageOptions[0];
  persistLiveSlippage(modeKey, pct);
}

// View Odds with Commission
const viewOddsWithCommission = ref();
async function handleViewOddsWithCommission(value) {
  try {
    let swalText = '';
    if (!userStore.user.viewOddsWithCommission) {
      swalText =
        '<h4>View Odds With Commission mode will display all odds with commission factored in, adjusted from the original price they were offered at. </h4>';
    } else {
      swalText =
        '<h4>Removing View Odds With Commission mode will display all odds without commission factored in, at the original price they were offered at. </h4>';
    }
    const result = await Swal.fire({
      html: swalText,
      showCancelButton: true,
      confirmButtonText: 'Update Display',
      confirmButtonColor: '#6f75b1',
    });
    if (result.isConfirmed) {
      updateViewOddsWithCommission(value);
    } else {
      viewOddsWithCommission.value = userStore.user.viewOddsWithCommission
        ? viewCommissionOptions[0]
        : viewCommissionOptions[1];
    }
  } catch {
    console.log('error updating commission');
  }
}

async function updateViewOddsWithCommission(value) {
  try {
    const fromValue = !!userStore.user.viewOddsWithCommission;
    const { request } = useAPI('user/viewOddsWithCommission/');
    const response = await request({
      display: value,
    });
    if (response.success) {
      userStore.user.viewOddsWithCommission = value.key === 'yes';
      trackSetting('view_odds_with_commission', fromValue, value.key === 'yes');
      viewOddsWithCommission.value =
        value.key === 'yes'
          ? viewCommissionOptions[0]
          : viewCommissionOptions[1];
      emitter.emit('showAlert', {
        type: 'profile-updated',
        shown: true,
        status: 'ok',
        title: 'Saved configuration',
        detail: '',
      });
    }
  } catch {
    console.error('error updating view odds with commission');
  }
}

// Sort By
const sortByValue = ref();

function canonicalSort() {
  return canonicalSortFromUser(userStore.user);
}

async function updateSortBy() {
  try {
    const fromSort = canonicalSort();
    const { request } = useAPI('user/updateSortBy/');
    const { get } = useAPI('user/getMe/');
    const response = await request({
      sortBy: sortByValue.value,
    });
    const getMeResponse = await get();
    userStore.setGetMe(getMeResponse.user);
    trackSetting('sort_games_by', fromSort, canonicalSort());
    emitter.emit('sortChange', {});
    emitter.emit('showAlert', {
      type: 'profile-updated',
      shown: true,
      status: 'ok',
      title: 'Saved configuration',
      detail: '',
    });
  } catch {
    console.error('error updating sort by ');
  }
}

// update email
async function saveEmail() {
  try {
    const result = await Swal.fire({
      html: '<h4>Do you want to save the changes?</h4>',
      showCancelButton: true,
      confirmButtonText: 'Save',
      confirmButtonColor: '#6f75b1',
    });

    if (!result.isConfirmed) {
      return;
    }

    loading.value = true;
    const { request } = useAPI('/user/updateEmail');
    const lowerCaseEmail = email.value.toLowerCase();
    const response = await request({
      email: lowerCaseEmail,
      currentPassword: emailPassword.value,
    });

    if (response.success) {
      emailPassword.value = '';
      emitter.emit('showAlert', {
        type: 'profile-updated',
        shown: true,
        status: 'ok',
        title: 'Your email has been updated',
        detail: 'Please check your email for a confirmation link',
      });
      await getSettings();
    } else {
      throw new Error(response.message);
    }
  } catch (error) {
    emitter.emit('showAlert', {
      type: 'profile-updated',
      shown: true,
      status: 'danger',
      title: 'Error',
      detail: error.message,
    });
  } finally {
    loading.value = false;
    getSettings();
  }
}

// Odds Types
const oddsType = ref();
function updateOddsType(oddsType) {
  Swal.fire({
    html: '<h4>Do you want to save the changes?</h4>',
    showCancelButton: true,
    confirmButtonText: 'Save',
    confirmButtonColor: '#6f75b1',
  }).then(async (result) => {
    if (result.isConfirmed) {
      loading.value = true;
      const fromOdds = userStore.user.oddsFormat;
      oddsType.value = oddsType;
      oddsType.id === '1'
        ? (userStore.user.oddsFormat = 'american')
        : oddsType.id === '2'
          ? (userStore.user.oddsFormat = 'decimal')
          : (userStore.user.oddsFormat = 'impliedProbability');

      const { request } = useAPI('/user/updateOddsFormat');
      await request({ oddsFormat: oddsType.key }).then(() => {
        // Track only after the save request resolves, so a failed save
        // doesn't record a format change that never took effect.
        trackSetting('display_odds', fromOdds, userStore.user.oddsFormat);
        //getMe function
        getSettings();
        loading.value = false;
      });
      emitter.emit('showAlert', {
        type: 'profile-updated',
        shown: true,
        status: 'ok',
        title: 'Saved configuration',
        detail: '',
      });
      // show alert
    }
  });
}

// Odds Views
const oddsView = ref();
async function updateOddsView(update) {
  try {
    const result = await Swal.fire({
      html: '<h4>Do you want to save the changes?</h4>',
      showCancelButton: true,
      confirmButtonText: 'Save',
      confirmButtonColor: '#6f75b1',
    });

    if (result.isConfirmed) {
      const fromView = userStore.user.sportsbookDefault ? 'sportsbook' : 'exchange';
      oddsView.value = update;
      loading.value = true;
      let sportsbookDefault =
        oddsView.value.key === 'sportsbook' ? true : false;
      const { request } = useAPI('/user/updateDefaultView');

      const apiResult = await request({ sportsbookDefault });
      userStore.user.sportsbookDefault = oddsView.value.key === 'sportsbook';
      trackSetting('default_odds_view', fromView, oddsView.value.key);
      getSettings();
      loading.value = false;
      emitter.emit('showAlert', {
        type: 'profile-updated',
        shown: true,
        status: 'ok',
        title: 'Saved configuration',
        detail: '',
      });
    }
  } catch (error) {
    console.error('Error updating odds view');
    // Handle error
  }
}

// Two-factor authentication (v2 verification service). The user has at most one
// active factor ('totp' | 'email', or '' = none yet). It lives in a separate
// credentials collection server-side, so we read it from GET /auth/method rather
// than the user object. Enrolling/switching is the two-door flow in the global
// TwoFactorEnroll modal (opened via the emitter); we refresh the method after.
const currentTwoFactorMethod = ref('');

const twoFactorStatusLabel = computed(() => {
  switch (currentTwoFactorMethod.value) {
    case 'totp':
      return 'Active — authenticator app';
    case 'email':
      return 'Active — email codes';
    default:
      return 'Not set up yet';
  }
});

async function refreshTwoFactorMethod() {
  try {
    currentTwoFactorMethod.value = (await getActiveMethod()) || '';
  } catch {
    currentTwoFactorMethod.value = '';
  }
}

function openTwoFactorEnroll() {
  emitter.emit('showTwoFactorEnroll');
}

//2FA Types
const twoFAType = ref();

// Rotation Numbers
const viewDisplayRotation = ref();
async function updateDisplayRotationNumbers(value) {
  try {
    const result = await Swal.fire({
      html: '<h4>Do you want to save the changes?</h4>',
      showCancelButton: true,
      confirmButtonText: 'Save',
      confirmButtonColor: '#6f75b1',
    });

    if (result.isConfirmed) {
      const fromValue = !!userStore.user.displayRotationNumbers;
      const { request } = useAPI('/user/displayRotationNumbers');
      const response = await request({
        display: value,
      });
      if (response.success) {
        userStore.user.displayRotationNumbers = value.key === 'yes';
        trackSetting('display_rotation_numbers', fromValue, value.key === 'yes');
        viewDisplayRotation.value =
          value.key === 'yes'
            ? displayRotationOptions[0]
            : displayRotationOptions[1];
        emitter.emit('showAlert', {
          type: 'profile-updated',
          shown: true,
          status: 'ok',
          title: 'Saved configuration',
          detail: '',
        });
      }
    }
  } catch {
    console.error('error updating display rotation setting');
  } finally {
    userSettingPopUp.value = false;
  }
}

// DGS Types
const dgsTypes = [
  { name: 'yes', key: true, id: '1' },
  { name: 'no', key: false, id: '2' },
];

const themes = [
  { name: 'Light Mode', key: 'day', id: '2' },
  { name: 'Night Mode', key: 'night', id: '1' },
];

// Lock status options for poly user
const lockStatusOptions = [
  { name: 'Unlocked', key: 'unlocked', id: '1' },
  { name: 'Locked', key: 'locked', id: '2' },
];

const selectedLockStatus = computed(() => {
  return lockStatusOptions.find(
    (option) => option.key === (isUserLocked.value ? 'locked' : 'unlocked'),
  );
});

const selectedTheme = computed(() => {
  return themes.find((theme) => theme.key === userStore.activeTheme);
});

function updateActiveTheme(value) {
  const fromValue = userStore.activeTheme;
  userStore.setActiveTheme(value.key);
  trackSetting('active_theme', fromValue, value.key);
}

const dgsType = ref({ name: 'yes', key: true, id: '1' });
async function updateDgsType(dgsType) {
  try {
    const result = await Swal.fire({
      html: '<h4>Do you want to Update these Settings?</h4>',
      showCancelButton: true,
      confirmButtonText: 'Save',
      confirmButtonColor: '#6f75b1',
    });
    if (result.isConfirmed) {
      loading.value = true;
      const { request } = useAPI('/user/toggleDgsSeeder');
      const response = await request({ dgsFormat: dgsType.key });
      getSettings();
    }
  } catch (error) {
    console.log('error from update dgs type ');
  } finally {
    loading.value = false;
  }
}

// Pro User
// Pro Mode is three-way: it opens the take bet slip in offer mode for pregame
// games, live games, both, or neither. The three options map onto the two
// booleans isPro (pregame) and isProLive (live). Order is kept Yes/No/Pregame
// Only so the existing [0]=Yes and [1]=No indices still hold.
const proUserOptions = [
  { name: 'Yes', key: 'yes', id: '1' },
  { name: 'No', key: 'no', id: '2' },
  { name: 'Pregame Only', key: 'pregame', id: '3' },
];
// Single source of truth for turning the two stored booleans into the selected
// option, so the initial load and the post-save refresh can't drift apart.
function resolveProUserOption(isPro, isProLive) {
  if (!isPro) return proUserOptions[1];
  return isProLive ? proUserOptions[0] : proUserOptions[2];
}
const isProUser = ref();
function updateIsPro(user) {
  Swal.fire({
    html: '<h4>Do you want to save the changes?</h4>',
    showCancelButton: true,
    confirmButtonText: 'Save',
    confirmButtonColor: '#6f75b1',
  }).then((result) => {
    if (result.isConfirmed) {
      const fromValue = resolveProUserOption(
        userStore.user.isPro,
        userStore.user.isProLive,
      ).name;
      isProUser.value = user;
      const isPro = user.key === 'yes' || user.key === 'pregame';
      const isProLive = user.key === 'yes';
      const { request } = useAPI('/user/updateProMode');
      request({ proMode: user }).then((response) => {
        userStore.user.isPro = isPro;
        userStore.user.isProLive = isProLive;
        trackSetting('pro_mode', fromValue, user.name);
        isProUser.value = resolveProUserOption(isPro, isProLive);
        //show alert
        emitter.emit('showAlert', {
          type: 'profile-updated',
          shown: true,
          status: 'ok',
          title: 'Saved configuration',
          detail: '',
        });
      });
    }
  });
}

// Send Order
const sendOrderToChatOptions = [
  { name: 'Yes', key: 'yes', id: '1' },
  { name: 'No', key: 'no', id: '2' },
];
const sendOrderToChat = ref();

// Confirmation Ticket
const confirmationTicketOptions = [
  { name: 'Yes', key: 'yes', id: '1' },
  { name: 'No', key: 'no', id: '2' },
];
const displayConfirmationTicket = ref();

// Default Arb Offer
const arbOfferOptions = [
  { name: 'On', key: 'on', id: '1' },
  { name: 'Off', key: 'off', id: '2' },
];
const defaultArbOfferSetting = ref(
  (() => {
    const stored = (() => {
      try {
        return localStorage.getItem('defaultArbOffer') === 'true';
      } catch (err) {
        return false;
      }
    })();
    const active = !!userStore.user?.defaultArbOffer || stored;
    return active ? arbOfferOptions[0] : arbOfferOptions[1];
  })(),
);

function updateDefaultArbOffer(selection) {
  Swal.fire({
    html: '<h4>Do you want to save the changes?</h4>',
    showCancelButton: true,
    confirmButtonText: 'Save',
    confirmButtonColor: '#6f75b1',
  }).then((result) => {
    if (!result.isConfirmed) return;

    const enabled = selection.key === 'on';
    const fromValue = !!userStore.user?.defaultArbOffer;
    trackSetting('default_arb_offer', fromValue, enabled);

    // Apply locally first — this preference drives only the Create Offer UI,
    // so a local source of truth is sufficient. The /user/updateDefaultArbOffer
    // endpoint may not exist yet in every environment; best-effort sync below.
    if (userStore.user) {
      userStore.user.defaultArbOffer = enabled;
    }
    try {
      localStorage.setItem('defaultArbOffer', enabled ? 'true' : 'false');
    } catch (err) {
      // ignore storage errors
    }
    defaultArbOfferSetting.value = selection;

    const { request } = useAPI('/user/updateDefaultArbOffer');
    request({ defaultArbOffer: enabled }).catch((err) => {
      // Server sync failed — local preference stays in effect.
      // eslint-disable-next-line no-console
      console.warn('[ArbOffer] default preference saved locally only:', err);
    });

    emitter.emit('showAlert', {
      type: 'profile-updated',
      shown: true,
      status: 'ok',
      title: 'Saved configuration',
      detail: '',
    });
  });
}

// Default quick-bet amount — the amount the take-bet quick (+) button adds on
// tap. Device-local preference; the take-bet modal reads these same keys via
// useStorage. One control sets both the Risk and To-Win buttons.
const quickBetOptions = [
  { name: '+100', key: '100', id: '1' },
  { name: '+250', key: '250', id: '2' },
  { name: '+500', key: '500', id: '3' },
  { name: '+1,000', key: '1000', id: '4' },
  { name: 'Max', key: 'max', id: '5' },
];
// Sentinel "no preset selected" — used when a custom amount is active so none
// of the preset pills highlight (the selector matches on .id).
const noPresetSelected = { name: '', key: '', id: 'none' };
const storedQuickBet = (() => {
  try {
    // The modal stores these via useStorage('...', '100'); a string default
    // uses vueuse's "string" serializer, which persists the RAW value (no
    // JSON quotes), so we read/write it raw here too.
    return localStorage.getItem('takebet-risk-quick-mode') || '100';
  } catch (err) {
    return '100';
  }
})();
// If the stored value isn't a known preset, it's a custom amount — leave the
// preset row unselected and pre-fill the custom input with it.
const storedPreset = quickBetOptions.find((o) => o.key === storedQuickBet);
const quickBetAmountSetting = ref(storedPreset || noPresetSelected);
const customQuickBet = ref(storedPreset ? '' : storedQuickBet);

// Shared persistence: write the RAW string value to both keys (matches the
// modal's "string"-serializer useStorage, which stores without JSON quotes).
function persistQuickBet(value) {
  try {
    localStorage.setItem('takebet-risk-quick-mode', value);
    localStorage.setItem('takebet-win-quick-mode', value);
  } catch (err) {
    // ignore storage errors
  }
  emitter.emit('showAlert', {
    type: 'profile-updated',
    shown: true,
    status: 'ok',
    title: 'Saved configuration',
    detail: '',
  });
}

// Current stored quick-bet amount, the true prior value for a change event's
// from_amount — works whether the prior value was a preset or a custom amount
// (the preset-pill key is empty while a custom amount is active).
function currentQuickBet() {
  try {
    return localStorage.getItem('takebet-risk-quick-mode') || '100';
  } catch {
    return '100';
  }
}

function updateQuickBetAmount(selection) {
  Swal.fire({
    html: '<h4>Do you want to save the changes?</h4>',
    showCancelButton: true,
    confirmButtonText: 'Save',
    confirmButtonColor: '#6f75b1',
  }).then((result) => {
    if (!result.isConfirmed) return;
    const fromQuickBet = currentQuickBet();
    PostHogUtils.capture('quick_add_default_changed', {
      from_amount: fromQuickBet,
      to_amount: selection.key,
      is_max: selection.key === 'max',
      is_custom: false,
      source: 'settings',
    });
    PostHogUtils.syncSettings({ quick_bet_amount: selection.key });
    persistQuickBet(selection.key);
    quickBetAmountSetting.value = selection;
    // Picking a preset overrides any custom amount.
    customQuickBet.value = '';
  });
}

function saveCustomQuickBet() {
  const amount = Math.round(parseFloat(customQuickBet.value));
  if (!Number.isFinite(amount) || amount <= 0) {
    emitter.emit('showAlert', {
      type: 'profile-updated',
      shown: true,
      status: 'error',
      title: 'Enter a valid amount',
      detail: 'Quick-bet amount must be a positive number.',
    });
    return;
  }
  PostHogUtils.capture('quick_add_default_changed', {
    from_amount: currentQuickBet(),
    to_amount: String(amount),
    is_max: false,
    is_custom: true,
    source: 'settings',
  });
  PostHogUtils.syncSettings({ quick_bet_amount: String(amount) });
  customQuickBet.value = String(amount);
  persistQuickBet(String(amount));
  // A custom amount is now active — clear the preset highlight.
  quickBetAmountSetting.value = noPresetSelected;
}

function updateDisplayConfirmationTicket(confirmationSetting) {
  Swal.fire({
    html: '<h4>Do you want to save the changes?</h4>',
    showCancelButton: true,
    confirmButtonText: 'Save',
    confirmButtonColor: '#6f75b1',
  }).then((result) => {
    if (result.isConfirmed) {
      // Store setting locally for now (backend API doesn't exist yet)
      const settingValue = confirmationSetting.key === 'yes';
      const fromValue = !!userStore.user.displayConfirmationTicket;

      // Update user store
      userStore.user.displayConfirmationTicket = settingValue;
      trackSetting('display_confirmation_ticket', fromValue, settingValue);

      // Store in localStorage for persistence
      localStorage.setItem(
        'displayConfirmationTicket',
        settingValue.toString(),
      );

      // Update the UI
      displayConfirmationTicket.value = confirmationSetting;

      // Show success message
      emitter.emit('showAlert', {
        type: 'profile-updated',
        shown: true,
        status: 'ok',
        title: 'Saved configuration',
        detail: '',
      });
    }
  });
}

function updateSendOrderToChat(sendOrder) {
  Swal.fire({
    html: '<h4>Do you want to save the changes?</h4>',
    showCancelButton: true,
    confirmButtonText: 'Save',
    confirmButtonColor: '#6f75b1',
  }).then((result) => {
    if (result.isConfirmed) {
      const fromValue = !!userStore.user.defaultSendOrderMessage;
      const { request } = useAPI('/user/updateDefaultSendOrderMessage');
      request({ sendOrderToChat: sendOrder }).then((response) => {
        if (response.success) {
          userStore.defaultSendOrderMessage = sendOrder;
          // Keep the user object in sync (like the other settings handlers) so a
          // second toggle this session reads an accurate fromValue.
          userStore.user.defaultSendOrderMessage = sendOrder.key === 'yes';
          sendOrderToChat.value = sendOrder;
          trackSetting('default_send_order_to_chat', fromValue, sendOrder.key === 'yes');
          emitter.emit('showAlert', {
            type: 'profile-updated',
            shown: true,
            status: 'ok',
            title: 'Saved configuration',
            detail: '',
          });
        }
        getSettings();
      });
    }
  });
}

// update email
const email = ref('');
const emailPassword = ref('');

async function saveSportsbookMinimumDisplay() {
  try {
    const result = await Swal.fire({
      html: '<h4>Do you want to save the changes?</h4>',
      showCancelButton: true,
      confirmButtonText: 'Save',
      confirmButtonColor: '#6f75b1',
    });
    if (result.isConfirmed) {
      loading.value = true;
      const { request } = useAPI('/user/updateSportsbookMinimumDisplay/');
      const response = await request({
        sportsbookMinimumDisplay: sportsbookMinimumDisplay.value,
      });
      if (response.success) {
        userStore.user.sportsbookMinimumDisplay =
          sportsbookMinimumDisplay.value;
        emitter.emit('showAlert', {
          type: 'profile-updated',
          shown: true,
          status: 'ok',
          title: 'Saved configuration',
          detail: '',
        });
      }
    }
  } catch (error) {
    console.log('error updating sportsbook minimum display');
  } finally {
    loading.value = false;
  }
}

// default offer
const defaultOffer = ref(0);
function saveDefaultOffer() {
  Swal.fire({
    html: '<h4>Do you want to save the changes?</h4>',
    showCancelButton: true,
    confirmButtonText: 'Save',
    confirmButtonColor: '#6f75b1',
  }).then((result) => {
    if (result.isConfirmed) {
      const { request } = useAPI('/user/updateDefaultOffer');
      request({ defaultOffer: defaultOffer.value }).then((response) => {
        // show alert
        emitter.emit('showAlert', {
          type: 'profile-updated',
          shown: true,
          status: 'ok',
          title: 'Saved configuration',
          detail: '',
        });
      });
    }
  });
}

const viewCreateOfferUnitMode = ref(createOfferUnitModeOptions[0]);
async function updateCreateOfferUnitMode(value) {
  const result = await Swal.fire({
    html: '<h4>Do you want to save the changes?</h4>',
    showCancelButton: true,
    confirmButtonText: 'Save',
    confirmButtonColor: '#6f75b1',
  });
  if (!result.isConfirmed) return;
  const fromValue = userStore.user.createOfferUnitMode || 'riskAUnit';
  const { request } = useAPI('/user/updateCreateOfferUnitMode');
  const response = await request({ createOfferUnitMode: value.key });
  if (response.success) {
    userStore.user.createOfferUnitMode = value.key;
    trackSetting('create_offer_unit_mode', fromValue, value.key);
    viewCreateOfferUnitMode.value = value;
    emitter.emit('showAlert', {
      type: 'profile-updated',
      shown: true,
      status: 'ok',
      title: 'Saved configuration',
      detail: '',
    });
  }
}

// default expiry
const defaultExpiry = ref(0);
function saveDefaultExpiry() {
  Swal.fire({
    html: '<h4>Do you want to save the changes?</h4>',
    showCancelButton: true,
    confirmButtonText: 'Save',
    confirmButtonColor: '#6f75b1',
  }).then((result) => {
    if (result.isConfirmed) {
      const fromExpiry = normalizeExpiryMinutes(userStore.user.defaultExpiry);
      const { request } = useAPI('/user/updateDefaultExpiry');
      request({ defaultExpiry: defaultExpiry.value }).then((response) => {
        if (response.success) {
          userStore.user.defaultExpiry = defaultExpiry.value;
          trackSetting(
            'default_order_expiry_minutes',
            fromExpiry,
            normalizeExpiryMinutes(defaultExpiry.value),
          );
        }
        // show alert
        emitter.emit('showAlert', {
          type: 'profile-updated',
          shown: true,
          status: 'ok',
          title: 'Saved configuration',
          detail: '',
        });
      });
    }
  });
}

// subscribe telegram
const telegramUsername = ref('');
function subscribeTelegram() {
  if (telegramUsername.value === '') {
    // show alert
    emitter.emit('showAlert', {
      shown: true,
      status: 'danger',
      title: 'Please enter your Telegram username',
    });
  } else {
    loading.value = true;

    const { request } = useAPI('/user/tickerChatRequest');
    request({ telegramUsername: telegramUsername.value })
      .then((request) => {
        if (request.message === 'success') {
          // show alert
          emitter.emit('showAlert', {
            shown: true,
            status: 'ok',
            title: 'Telegram username submitted',
            detail: 'a developer will reach out to you with more information',
          });
          telegramUsername.value = '';
        }
      })
      .catch((error) => {
        // show alert
        emitter.emit('showAlert', {
          shown: true,
          status: 'danger',
          title: 'Error',
          detail: error,
        });
      })
      .finally(() => {
        loading.value = false;
      });
  }
}
function closeAccount() {
  Swal.fire({
    title: 'Are you sure?',
    text: 'This action cannot be undone',
    showCancelButton: true,
    confirmButtonText: 'Confirm',
    confirmButtonColor: '#6f75b1',
    icon: 'warning',
  }).then((result) => {
    if (result.isConfirmed) {
      const { request } = useAPI('affiliate/closeAffiliateAccount');
      request({ referredID: userStore.user.id }).then((response) => {
        if (response.message === 'success') {
          emitter.emit('showAlert', {
            type: 'profile-updated',
            shown: true,
            status: 'ok',
            title: 'Your account has been closed',
            detail: '',
          });
          userStore.logOut();
          router.push('/log-in');
        }
      });
    }
  });
}

// Get current lock status for poly user
async function getLockStatus() {
  try {
    const { get } = useAPI('/user/getLockStatus');
    const response = await get();
    if (response.success) {
      isUserLocked.value = response.locked;
    }
  } catch (error) {
    console.error('Error fetching lock status:', error);
  }
}

// Update lock status for poly user
async function updateLockStatus(value) {
  try {
    const result = await Swal.fire({
      html: `<h4>Update Account Status</h4>
      <p class="confirmation-text">
        Are you sure you want to ${value.key === 'locked' ? 'lock' : 'unlock'} your account?
      </p>`,
      showCancelButton: true,
      confirmButtonText:
        value.key === 'locked' ? 'Lock Account' : 'Unlock Account',
      confirmButtonColor: '#6f75b1',
    });

    if (result.isConfirmed) {
      loading.value = true;
      const { request } = useAPI('/user/updateLockStatus');
      const response = await request({
        locked: value.key === 'locked',
      });
      if (response.success) {
        isUserLocked.value = value.key === 'locked';
        emitter.emit('showAlert', {
          type: 'profile-updated',
          shown: true,
          status: 'ok',
          title: 'Account Status Updated',
          detail: `Your account has been ${value.key === 'locked' ? 'locked' : 'unlocked'}`,
        });
      } else {
        throw new Error(response.message || 'Failed to update account status');
      }
    }
  } catch (error) {
    emitter.emit('showAlert', {
      type: 'profile-updated',
      shown: true,
      status: 'danger',
      title: 'Error',
      detail: error.message || 'Failed to update account status',
    });
  } finally {
    loading.value = false;
  }
}

const RESEND_COOLDOWN_KEY = 'resendConfirmationEmailCooldownEnd';

function startResendCooldown(seconds = 60) {
  resendCooldown.value = seconds;
  // Store the end timestamp in localStorage
  const endTime = Date.now() + seconds * 1000;
  localStorage.setItem(RESEND_COOLDOWN_KEY, endTime.toString());

  if (resendCooldownInterval) {
    clearInterval(resendCooldownInterval);
  }
  resendCooldownInterval = setInterval(() => {
    resendCooldown.value--;
    if (resendCooldown.value <= 0) {
      clearInterval(resendCooldownInterval);
      resendCooldownInterval = null;
      localStorage.removeItem(RESEND_COOLDOWN_KEY);
    }
  }, 1000);
}

function restoreResendCooldown() {
  const storedEndTime = localStorage.getItem(RESEND_COOLDOWN_KEY);
  if (storedEndTime) {
    const remainingMs = parseInt(storedEndTime, 10) - Date.now();
    const remainingSeconds = Math.ceil(remainingMs / 1000);
    if (remainingSeconds > 0) {
      startResendCooldown(remainingSeconds);
    } else {
      localStorage.removeItem(RESEND_COOLDOWN_KEY);
    }
  }
}

onUnmounted(() => {
  if (resendCooldownInterval) {
    clearInterval(resendCooldownInterval);
  }
});

async function resendConfirmationEmail() {
  const { request } = useAPI('/user/sendVerificationEmail');
  const response = await request();
  if (response.success) {
    startResendCooldown();
    emitter.emit('showAlert', {
      shown: true,
      status: 'ok',
      title: 'Confirmation email sent',
    });
  } else {
    emitter.emit('showAlert', {
      shown: true,
      status: 'danger',
      title: 'Error',
      detail: response.message,
    });
  }
}
</script>

<style scoped lang="scss">

@use 'sass:map';
@import '@/scss/variables.scss';
@import '@/scss/theme/variables.scss';

// Modern Account Settings Styles
.account-settings-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.25rem 1rem;

  @media screen and (max-width: map.get($grid-breakpoints, md)) {
    padding: 1rem 0.75rem;
  }
}

// Portfolio Section
.portfolio-section {
  margin-bottom: 1rem;
}

.portfolio-card {
  background:
    linear-gradient(135deg, rgba(99, 102, 241, 0.05), rgba(139, 92, 246, 0.03)),
    var(--white_01);
  border: 1px solid var(--grey-1901);
  border-radius: 14px;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  box-shadow:
    0 1px 2px rgba(16, 24, 64, 0.04),
    0 4px 16px rgba(16, 24, 64, 0.05);
}

.portfolio-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.portfolio-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: $grey-2300;
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.portfolio-balance {
  font-size: 1.625rem;
  font-weight: 800;
  color: $blue-1100;
  line-height: 1.1;
  letter-spacing: -0.5px;
}

.portfolio-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.portfolio-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border-radius: 10px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease,
    border-color 0.15s ease;
  border: none;
  white-space: nowrap;

  .btn-icon {
    font-size: 0.75rem;
  }

  &:active {
    transform: translateY(0);
  }
}

// Primary deposit — gradient matching the deposit modal CTA for brand cohesion.
.btn-deposit {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(99, 102, 241, 0.42);
  }
}

// Secondary actions — refined ghost buttons with a subtle lift on hover.
.btn-withdraw,
.btn-export,
.btn-history {
  background: var(--white_01);
  color: $blue-1100;
  border: 1px solid var(--grey-1901);

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(99, 102, 241, 0.4);
    box-shadow: 0 2px 10px rgba(16, 24, 64, 0.08);
  }
}

.transaction-history-title {
  margin: 0 0 0.75rem;
  font-size: 18px;
  font-weight: 700;
  color: $color-primary;

  // Night mode: $color-primary is a dark navy that's unreadable on the dark
  // modal background. Use the light primary the rest of the night UI uses.
  [theme='night'] & {
    color: $color-primary_01;
  }
}

@media screen and (max-width: map.get($grid-breakpoints, sm)) {
  .portfolio-card {
    padding: 0.625rem 0.875rem;
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .portfolio-info {
    justify-content: space-between;
  }

  .portfolio-actions {
    justify-content: flex-end;
  }

  .portfolio-btn {
    flex: 1;
    padding: 0.4rem 0.5rem;
    font-size: 0.75rem;
  }
}

// Page Header
.page-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid $grey-1901;

  .header-content {
    display: flex;
    align-items: center;
    gap: 1rem;

    .header-icon {
      font-size: 2rem;
      color: $color-primary_01;
      opacity: 0.9;
    }

    .page-title {
      font-size: 1.5rem;
      font-weight: bold;
      color: $blue-1100;
      margin: 0;
      line-height: 1.2;
    }

    .page-subtitle {
      font-size: 0.875rem;
      color: $grey-2300;
      margin: 0.125rem 0 0 0;
      opacity: 0.8;
    }
  }

  @media screen and (max-width: map.get($grid-breakpoints, sm)) {
    margin-bottom: 1rem;

    .header-content {
      .header-icon {
        font-size: 1.75rem;
      }

      .page-title {
        font-size: 1.25rem;
      }

      .page-subtitle {
        font-size: 0.8125rem;
      }
    }
  }
}

// Settings Grid
.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.25rem;

  @media screen and (max-width: map.get($grid-breakpoints, lg)) {
    grid-template-columns: 1fr;
  }

  @media screen and (max-width: map.get($grid-breakpoints, sm)) {
    gap: 1rem;
  }
}

// Settings Section
.settings-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .section-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;

    .section-icon {
      font-size: 1.1rem;
      color: $color-primary_01;
    }

    .section-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: $blue-1100;
      margin: 0;
    }
  }

  &.danger-zone {
    .section-header {
      .danger-icon {
        color: $color-secondary;
      }

      .section-title {
        color: $color-secondary;
      }
    }
  }
}

// Settings Card
.settings-card {
  background: $grey-500_03;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  border: 1px solid $grey-1901;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &.danger-card {
    border-color: rgba($color-secondary, 0.3);
    background: rgba($color-secondary, 0.02);
  }

  [theme='night'] & {
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.4),
      0 1px 2px rgba(0, 0, 0, 0.3);
    border-color: $grey-601;

    &:hover {
      box-shadow:
        0 4px 12px rgba(0, 0, 0, 0.5),
        0 2px 4px rgba(0, 0, 0, 0.4);
    }
  }

  @media screen and (max-width: map.get($grid-breakpoints, sm)) {
    padding: 0.875rem;
  }
}

// Setting Item
.setting-item {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 0.875rem 0;
  border-bottom: 1px solid $grey-1901;

  &.setting-row {
    flex-direction: row;
    gap: 2rem;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }

  .setting-label {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;

    .label-text {
      font-size: 0.875rem;
      font-weight: 600;
      color: $blue-900;
      display: flex;
      align-items: center;
      gap: 0.375rem;
    }

    .label-subtext {
      font-size: 0.75rem;
      color: $grey-2300;
      opacity: 0.7;
      margin-top: 0;

      &.danger-text {
        color: $color-secondary;
        opacity: 0.9;
      }

      &.warning-text {
        color: $yellow-300;
        font-weight: 700 !important;
      }

      &.success-text {
        color: $green-400;
      }
    }
  }

  .setting-control {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .status-badge {
      align-self: flex-start;
    }

    .selector,
    .modern-select {
      width: 100%;
    }

    .input-group {
      width: 100%;
    }

    #user-verification-email {
      background: none !important;
      border-bottom: 0 !important;
      border-top: 0 !important;
      &:focus {
        border-bottom: 0 !important;
        border-top: 0 !important;
      }
    }
  }

  &.password-section {
    .password-actions {
      display: flex;
      gap: 0.5rem;
      width: 100%;
      flex-wrap: wrap;

      @media screen and (max-width: map.get($grid-breakpoints, sm)) {
        flex-direction: column;
      }
    }
    // The "your password failed a security check" nudge. The signal lives in
    // the panel (tint + left accent bar) and the pill, so the buttons stay
    // ordinary buttons instead of a ring-around-an-invisible-slab.
    &.needs-attention {
      position: relative;
      margin: 0.625rem -0.375rem -0.125rem;
      padding: 0.875rem 0.875rem 0.9375rem 1rem;
      border-radius: 8px;
      border: 1px solid rgba(247, 147, 26, 0.35);
      background: rgba(247, 147, 26, 0.06);

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        border-radius: 8px 0 0 8px;
        background: $yellow-300;
      }

      .label-subtext {
        color: #7a5a2c;
        opacity: 1;
      }
    }

    .attention-pill {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      margin-left: 0.5rem;
      padding: 0.1rem 0.5rem;
      border-radius: 999px;
      background: rgba(247, 147, 26, 0.16);
      color: #a75c00;
      font-size: 0.6563rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;

      svg {
        font-size: 0.625rem;
      }
    }

    .password-actions {
      margin-top: 0.125rem;
    }
  }
}

// Status Badges
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  .badge-icon {
    font-size: 0.7rem;
  }

  &.badge-enabled {
    background: rgba($green, 0.15);
    color: $green;

    &.badge-critical {
      background: rgba($color-primary, 0.15);
      color: $color-primary;
    }
  }

  &.badge-disabled {
    background: rgba($grey-2300, 0.15);
    color: $grey-2300;
  }

  &.badge-warning {
    background: rgba($color-secondary-2, 0.15);
    color: $color-secondary-2;
  }
}

.resend-confirmation-text {
  color: $green-400;
  font-size: 0.8125rem;
  font-weight: 500;
  margin-top: 0.25rem;
}

// Modern Buttons
.modern-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  .btn-icon {
    font-size: 0.8125rem;
  }

  &:disabled {
    background: $blue-901_03 !important;
    color: $grey-2400_01 !important;
    cursor: not-allowed !important;
    transform: none !important;
    filter: none !important;
    box-shadow: none !important;
  }

  &.btn-primary {
    background: $color-primary_03;
    color: $white;

    &:hover {
      filter: brightness(0.92);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }

    &:active {
      transform: translateY(0);
      filter: brightness(0.88);
    }
  }

  &.btn-secondary {
    background: $color-primary_02;
    color: $white;

    &:hover {
      filter: brightness(0.92);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
  }

  &.btn-outline {
    background: transparent;
    color: $color-primary;
    border: 1.5px solid $color-primary;

    &:hover {
      background: $color-primary;
      color: $white;
      transform: translateY(-1px);
    }

    // In night theme the card is dark navy and $color-primary is nearly the
    // same navy, which made this button (e.g. "Reset Password") invisible — it
    // sat next to a white-text secondary button so only this one vanished. Use
    // a bright blue so the outline reads against the dark card.
    [theme='night'] & {
      color: $blue-500;
      border-color: $blue-500;

      &:hover {
        background: $blue-500;
        color: $white;
      }
    }
  }

  // Amber primary, used for the one action the page is actively nudging.
  &.btn-attention {
    background: $yellow-300;
    color: #1c1330;

    .btn-icon {
      color: #1c1330;
    }

    &:hover {
      filter: brightness(1.06);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
  }

  &.btn-danger {
    background: $color-secondary;
    color: $white;

    &:hover {
      filter: brightness(0.92);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba($color-secondary, 0.3);
    }
  }

  @media screen and (max-width: map.get($grid-breakpoints, sm)) {
    width: 100%;
    justify-content: center;
  }
}

// Modern Inputs
.modern-input {
  flex: 1;
  padding: 0.5rem 0.875rem;
  border: 1.5px solid $grey-1901;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: $color-primary_01;
  background: $white;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 2px rgba($color-primary, 0.1);
  }

  &::placeholder {
    color: $grey-2300;
    opacity: 0.6;
  }

  &.border-warning {
    border: 2px solid $yellow-300 !important;
  }

  &.border-success {
    border: 2px solid $green-400 !important;
  }

  [theme='night'] & {
    background: $white_07;
    border-color: $grey-601;
    color: $color-primary_01;

    &:focus {
      border-color: $color-primary_04;
      box-shadow: 0 0 0 2px rgba($color-primary_04, 0.2);
    }

    &::placeholder {
      color: $grey-300_01;
      opacity: 0.6;
    }
  }
}

.readonly-value {
  font-size: 0.8125rem;
  font-weight: 600;
  color: $color-primary_01;
  opacity: 0.8;
}

.modern-select {
  width: 100%;
  padding: 0.5rem 0.875rem;
  border: 1.5px solid $grey-1901;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: $color-primary_01;
  background: $blue-901_04;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 2px rgba($color-primary, 0.1);
  }
}

// Custom slider so the fill tracks the thumb exactly and the thumb reaches
// both ends flush (the native control insets the thumb and draws its own end
// cap, leaving a dead stub at max). The gradient is painted on the TRACK
// pseudo-element — never the input box, which the browser overlays with its
// own geometry. --fill uses the same thumb-inset math the browser uses to
// position the 16px thumb. Verified by rendered screenshots at 0/5/10.
.slippage-slider {
  // Track/thumb colors as local custom props so night mode can retint every
  // engine-specific pseudo-element in one place. In night, $color-primary
  // (#272d58) and $grey-1901 (#272d58) both collapse into the card navy,
  // leaving the whole control invisible; $blue-500 is the established
  // "bright blue on the dark card" accent in this file.
  --slider-fill: #{$color-primary};
  --slider-track: #{$grey-1901};

  [theme='night'] & {
    --slider-fill: #{$blue-500};
    --slider-track: rgba(255, 255, 255, 0.14);
  }

  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 16px;
  margin: 0;
  // Global main.scss `input` styling paints a padded box with transparent
  // top/bottom borders and a navy border-bottom on :focus (the "blue bar").
  // Zero all of it — this control draws its own track.
  padding: 0;
  border: none;
  background: transparent;
  outline: none;
  cursor: pointer;

  // Chromium gives sliders :focus-visible even from a mouse click — kill the
  // ring in every state/engine.
  &:hover,
  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    box-shadow: none;
    border: none;
  }
  &::-moz-focus-outer {
    border: 0;
  }

  &::-webkit-slider-runnable-track {
    height: 8px;
    border-radius: 4px;
    background: linear-gradient(
      to right,
      var(--slider-fill) 0%,
      var(--slider-fill) var(--fill, 8px),
      var(--slider-track) var(--fill, 8px),
      var(--slider-track) 100%
    );
  }
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    margin-top: -4px;
    border-radius: 50%;
    background: var(--slider-fill);
    cursor: pointer;
  }
  &::-moz-range-track {
    height: 8px;
    border-radius: 4px;
    background: linear-gradient(
      to right,
      var(--slider-fill) 0%,
      var(--slider-fill) var(--fill, 8px),
      var(--slider-track) var(--fill, 8px),
      var(--slider-track) 100%
    );
  }
  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border: none;
    border-radius: 50%;
    background: var(--slider-fill);
    cursor: pointer;
  }
}

// Live-slippage example reference: 2 columns on mobile, 4 on wider screens
// (8 lines, so both fill evenly with no ragged row and no overflow).
.slippage-examples {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.4rem 0.75rem;
  margin-top: 0.625rem;
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;

  @media screen and (min-width: map.get($grid-breakpoints, sm)) {
    grid-template-columns: repeat(4, 1fr);
  }

  .slippage-example-cell {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.3rem;

    .req {
      opacity: 0.7;
    }
    .arrow {
      color: $grey-2300;
    }
    .max {
      font-weight: 700;
    }

    // The odds inherit the page's default (dark navy) text color, which is
    // unreadable on the night card. $color-primary_01 is the same light
    // text the rest of the night UI uses; day resolves it to the usual navy.
    [theme='night'] & {
      .req,
      .max {
        color: $color-primary_01;
      }
    }
  }
}

// Input Group
.input-group {
  display: flex;
  gap: 0.5rem;
  width: 100%;

  .modern-input {
    flex: 1;
    border-radius: 6px;
  }

  .modern-btn {
    flex-shrink: 0;
    border-radius: 6px;
    padding-left: 0.875rem;
    padding-right: 0.875rem;
  }

  @media screen and (max-width: map.get($grid-breakpoints, sm)) {
    flex-direction: column;

    .modern-input,
    .modern-btn {
      width: 100%;
    }
  }
}

// Selector Override
.selector {
  font-weight: 600 !important;
  text-align: left;
  border-radius: 6px;
  border: 1.5px solid $grey-1901 !important;
  height: 40px !important;
  color: $color-primary_01 !important;
  width: 100%;
  cursor: pointer;
  padding: 0 0.875rem;
  background: $blue-901_04;
  transition: all 0.2s ease;

  &:hover {
    border-color: $color-primary !important;
  }
}

// Legacy support (keeping old classes for compatibility)
// .input-container styles are handled by setting-item classes now

.settings-save {
  background: $color-primary_03;
  border: $outline-02;
  border-left: none;
  border-radius: 0 8px 8px 0;
}

.password-button {
  &.reset {
    background-color: $color-primary_03;
  }
}

.confirmation-text {
  color: $grey-2505;
  font-size: 0.9em;
}

// Responsive adjustments
@media (max-width: 576px) {
  .setting-item {
    .setting-control {
      flex-direction: column;
      align-items: stretch;
    }
  }
}

// ---------------------------------------------------------------------------
// Night theme — Security card
//
// The dark palette collapsed every control onto the card colour: btn-primary
// (#272d58) sits ~1.1:1 against the card (#222744), so buttons read as empty
// fields, and the three adjacent rows each shouted in a different fully
// saturated accent (green ring, orange ring, blue outline) with no hierarchy.
//
// This block gives night mode ONE raised-control surface, one filled primary,
// one quiet ghost, and turns status colours into tinted chips instead of
// full-strength rings. Day theme is deliberately untouched.
// ---------------------------------------------------------------------------
[theme='night'] {
  $n-surface: #2c3358;
  $n-surface-hover: #363e6d;
  $n-border: rgba(255, 255, 255, 0.14);
  $n-border-strong: rgba(255, 255, 255, 0.22);
  $n-indigo: #4e67df;
  $n-indigo-hover: #6178e8;
  $n-amber: #f7931a;
  $n-amber-text: #ffb45c;
  $n-green-text: #4ade80;
  $n-body: #b9c0e4;

  .security-section {
    .settings-card {
      background: #232849;
      border-color: rgba(255, 255, 255, 0.07);
    }

    .setting-item {
      border-bottom-color: rgba(255, 255, 255, 0.07);
    }

    .setting-label .label-subtext {
      color: $n-body;
      opacity: 1;
    }

    // Controls are sized to their content — a full-bleed navy bar reads as a
    // disabled input, not a button.
    .setting-control > .modern-btn {
      align-self: flex-start;
    }

    // --- Buttons -----------------------------------------------------------
    .modern-btn {
      padding: 0.5rem 0.9375rem;
      border: 1px solid transparent;

      &.btn-primary {
        background: $n-indigo;
        border-color: rgba(255, 255, 255, 0.18);
        color: $white;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);

        &:hover {
          background: $n-indigo-hover;
          filter: none;
        }
      }

      // Quiet companion action — visible as a control, never competing with
      // the primary for attention.
      &.btn-outline {
        background: $n-surface;
        border: 1px solid $n-border;
        color: #d3d8f5;

        &:hover {
          background: $n-surface-hover;
          border-color: $n-border-strong;
          color: $white;
        }
      }

      // The one nudged action in the card. Solid amber with dark text beats a
      // 2px amber ring around an invisible button by a mile.
      &.btn-attention {
        border-color: rgba(255, 255, 255, 0.25);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
      }
    }

    // --- Email row: neutral field, status lives in the chip ----------------
    .modern-input {
      background: $n-surface;
      border: 1px solid $n-border;

      &.border-success,
      &.border-warning {
        border-width: 1px !important;
      }

      &.border-success {
        border-color: rgba(74, 222, 128, 0.35) !important;
      }

      &.border-warning {
        border-color: rgba(247, 147, 26, 0.4) !important;
      }
    }

    .setting-label .label-subtext {
      &.success-text,
      &.warning-text {
        align-self: flex-start;
        margin-top: 0.25rem;
        padding: 0.125rem 0.5rem;
        border-radius: 999px;
        font-size: 0.6875rem;
        font-weight: 600 !important;
        letter-spacing: 0.02em;
      }

      &.success-text {
        color: $n-green-text;
        background: rgba(74, 222, 128, 0.12);
      }

      &.warning-text {
        color: $n-amber-text;
        background: rgba(247, 147, 26, 0.14);
      }
    }

    .text-success {
      color: $n-green-text !important;
    }

    // --- Password block: colour only, structure lives in the base rules -----
    .password-section {
      &.needs-attention {
        border-color: rgba(247, 147, 26, 0.28);
        background: rgba(247, 147, 26, 0.07);

        .label-subtext {
          color: #e4c69a;
        }
      }

      .attention-pill {
        color: $n-amber-text;
      }
    }
  }
}

// Create Offer Unit Mode tooltip grid (rendered in the InformationalTooltip
// `wide` slot). Colors use theme CSS vars so it adapts to day/night like the
// popper itself.
.unit-mode-tip {
  font-size: 12px;

  &__lead {
    margin-bottom: 4px;
    text-align: center;

    strong {
      color: var(--blue-100);
    }
  }

  &__table {
    border-collapse: collapse;
    width: 100%;
    font-size: 11px;

    th,
    td {
      padding: 3px 5px;
      text-align: center;
      white-space: nowrap;
    }

    thead th {
      color: var(--blue-100);
      font-weight: 700;
      border-bottom: 1px solid var(--grey-101);
    }

    tbody th {
      text-align: left;
      font-weight: 700;
    }

    tbody tr + tr th,
    tbody tr + tr td {
      border-top: 1px solid var(--grey-101);
    }
  }
}

/* ---- Telegram betting section ---- */
.telegram-setting__form {
  display: flex;
  align-items: center;
  gap: 8px;
}

.telegram-setting__input {
  width: 160px;
  padding: 6px 10px;
  border: 1px solid var(--border-color, rgba(128, 128, 128, 0.35));
  border-radius: 6px;
  background: var(--input-bg, #fff);
  color: var(--text-color, #17212b);
  font-size: 0.85rem;
  font-weight: 600;
}

.telegram-setting__toggle {
  padding: 6px 14px;
  border: 1px solid #2b7fc4;
  border-radius: 6px;
  background: rgba(42, 171, 238, 0.12);
  color: #2b7fc4;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: rgba(42, 171, 238, 0.24);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.telegram-setting__status {
  font-size: 0.85rem;
  font-weight: 600;
  opacity: 0.85;

  &--ok {
    color: #12855c;
  }
}

.telegram-setting__inline-link {
  color: #2b7fc4;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.telegram-setting__steps {
  margin: 4px 0 0;
  padding-left: 18px;
  font-size: 0.82rem;
  opacity: 0.8;

  li {
    margin-bottom: 2px;
  }

  code {
    padding: 0 4px;
    border-radius: 3px;
    background: rgba(128, 128, 128, 0.18);
    font-size: 0.78rem;
  }
}

.telegram-setting__error {
  display: block;
  margin-top: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #b4442f;
}
</style>
